/**
 * Turning Points — when is a stock or index about to change direction?
 *
 * Daily resolution only (the app's data is daily bars; 5/15-minute and hourly are not
 * available). Base algorithm: DeMark TD Sequential — Setup 9 with perfection, TDST
 * lines, Countdown 13 with its qualifier — plus four complementary exhaustion detectors.
 * Every detector is measured on this ticker's own history against the base rate, and
 * the page shows those numbers next to today's reading.
 */

import { useState } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardContent, Chip, CircularProgress, Alert, Grid,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Select, MenuItem, FormControl, InputLabel,
  Divider, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { Search, TrendingUp, TrendingDown, CheckCircle, Cancel, HelpOutline, InfoOutlined, ExpandMore, EmojiEvents } from '@mui/icons-material';
import {
  ComposedChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine, Scatter, CartesianGrid, Legend,
} from 'recharts';
import apiService from '../../services/api';
import { WinnersSummary } from './TurningPointsRace';
import ExhaustionHeatmap from './ExhaustionHeatmap';

const dirColor = (d: string) => (d === 'up' ? '#10B981' : '#EF4444');
const DirChip = ({ d, label }: { d: string; label?: string }) => (
  <Chip size="small" icon={d === 'up' ? <TrendingUp sx={{ fontSize: 13 }} /> : <TrendingDown sx={{ fontSize: 13 }} />}
    label={label || (d === 'up' ? 'turn UP' : 'turn DOWN')}
    sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${dirColor(d)}22`, color: dirColor(d), fontWeight: 'bold' }} />
);
const pct = (v?: number | null) => (v == null ? '—' : `${v.toFixed(1)}%`);

function Verdict({ cur }: { cur: any }) {
  const v: string = cur.verdict;
  const color = v.includes('UP') ? '#10B981' : v.includes('DOWN') ? '#EF4444' : v === 'conflicting' ? '#F59E0B' : undefined;
  return (
    <Card sx={{ border: '1px solid', borderColor: color || 'divider', height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">Signals fired in the last 5 bars — across all 13 methods (the table below shows every method's reading, fired or not)</Typography>
        <Typography variant="h5" fontWeight="bold" sx={{ color: color || 'text.primary', textTransform: 'uppercase' }}>{v}</Typography>
        <Typography variant="caption" color="text.secondary">{cur.signals_up} detector{cur.signals_up === 1 ? '' : 's'} say up · {cur.signals_down} say down</Typography>
        <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary">Counting only detectors proven on this ticker</Typography>
          <Typography variant="body1" fontWeight="bold" sx={{ color: cur.proven_verdict.includes('UP') ? '#10B981' : cur.proven_verdict.includes('DOWN') ? '#EF4444' : cur.proven_verdict.startsWith('conflicting') ? '#F59E0B' : 'text.secondary' }}>
            {cur.proven_verdict}
          </Typography>
        </Box>
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {cur.recent_signals.length === 0 && <Typography variant="body2" color="text.disabled">No detector fired in the last 5 bars.</Typography>}
          {cur.recent_signals.map((s: any, i: number) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DirChip d={s.direction} />
              <Typography variant="body2">{s.name}</Typography>
              {s.proven && <Tooltip title="This detector's hit rate on this ticker is above the base rate at 95% confidence"><Chip size="small" label="proven" color="success" variant="outlined" sx={{ height: 16, fontSize: '0.58rem' }} /></Tooltip>}
              <Typography variant="caption" color="text.disabled">{s.bars_ago === 0 ? 'today' : `${s.bars_ago} bar${s.bars_ago > 1 ? 's' : ''} ago`} · {s.date}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function TDState({ cur }: { cur: any }) {
  const a = cur.active_setup;
  const n = Math.abs(cur.td_setup_count);
  const rising = cur.td_setup_count > 0;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">DeMark TD Sequential</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ color: n >= 7 ? '#F59E0B' : 'text.primary' }}>Setup {Math.min(n, 9)}/9</Typography>
          <Typography variant="body2" color="text.secondary">{cur.td_setup_direction}{n > 9 ? ` · extended to ${n}` : ''}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.4, mt: 0.5 }}>
          {Array.from({ length: 9 }).map((_, i) => (
            <Box key={i} sx={{ width: 18, height: 8, borderRadius: 1, bgcolor: i < n ? (rising ? '#EF4444' : '#10B981') : 'action.disabledBackground' }} />
          ))}
        </Box>
        <Typography variant="caption" color="text.disabled">
          {rising ? 'Rising: nine closes above the close 4 bars earlier completes a SELL setup' : n > 0 ? 'Falling: nine closes below the close 4 bars earlier completes a BUY setup' : 'No count in progress'}
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        {a ? (
          <>
            <Typography variant="body2" fontWeight="bold">
              Active {a.direction.toUpperCase()} setup · completed {a.setup_completed} ({a.bars_since_setup} bars ago)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mt: 0.7 }}>
              <Tooltip title="Bar 8 or 9 exceeded the extremes of bars 6 and 7. DeMark treats an imperfected 9 as unfinished — price tends to continue until it perfects.">
                <Chip size="small" icon={a.perfected ? <CheckCircle sx={{ fontSize: 13 }} /> : <Cancel sx={{ fontSize: 13 }} />}
                  label={a.perfected ? `Perfected ${a.perfected_at}` : 'Not perfected'} color={a.perfected ? 'success' : 'default'} variant="outlined" sx={{ height: 22, fontSize: '0.68rem' }} />
              </Tooltip>
              <Tooltip title={`TDST ${a.tdst_role}: the setup's extreme. A close through it means the trend is stronger than the exhaustion reading — the setup is void.`}>
                <Chip size="small" label={`TDST ${a.tdst_role} $${a.tdst}${a.tdst_broken_at ? ` · broken ${a.tdst_broken_at}` : ' · holding'}`}
                  color={a.tdst_broken_at ? 'error' : 'info'} variant="outlined" sx={{ height: 22, fontSize: '0.68rem' }} />
              </Tooltip>
              <Tooltip title="Thirteen closes beyond the low/high two bars earlier (not necessarily consecutive) after the setup. Bar 13 must also satisfy the qualifier against bar 8's close.">
                <Chip size="small" label={`Countdown ${a.countdown}/13 · ${a.countdown_status}${a.countdown_done_at ? ` ${a.countdown_done_at}${a.countdown_qualified === false ? ' (unqualified)' : ''}` : ''}`}
                  color={a.countdown_status === 'complete' ? 'warning' : a.countdown_status === 'cancelled' ? 'default' : 'primary'} variant="outlined" sx={{ height: 22, fontSize: '0.68rem' }} />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.4, mt: 0.7 }}>
              {Array.from({ length: 13 }).map((_, i) => (
                <Box key={i} sx={{ width: 12, height: 6, borderRadius: 1, bgcolor: i < a.countdown ? '#A855F7' : 'action.disabledBackground' }} />
              ))}
            </Box>
          </>
        ) : <Typography variant="body2" color="text.disabled">No completed setup in the window.</Typography>}
      </CardContent>
    </Card>
  );
}

function Measured({ det, base, params }: { det: Record<string, any>; base: any; params: any }) {
  const Cell = ({ side, d }: { side: 'up' | 'down'; d: any }) => {
    const m = d[side];
    if (!m.firings) return <TableCell sx={{ color: 'text.disabled', fontSize: '0.72rem' }}>no firings</TableCell>;
    const good = m.lift != null && m.lift > 0 && m.reliable;
    return (
      <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
        <Tooltip title={`${m.firings} firings · 95% CI ${m.ci95?.[0]}–${m.ci95?.[1]}% · base rate ${m.base_rate_pct}%${m.reliable ? '' : ` · fewer than ${params.min_firings} firings — not reliable`}`}>
          <span>
            <b style={{ color: good ? '#10B981' : m.lift != null && m.lift < 0 && m.reliable ? '#EF4444' : undefined }}>{pct(m.hit_rate_pct)}</b>
            <span style={{ opacity: 0.6 }}> vs {pct(m.base_rate_pct)}</span>
            <span style={{ marginLeft: 6, opacity: 0.8 }}>({m.lift > 0 ? '+' : ''}{m.lift})</span>
            <span style={{ marginLeft: 6, opacity: 0.5 }}>n={m.firings}{m.reliable ? '' : ' ⚠'}</span>
          </span>
        </Tooltip>
      </TableCell>
    );
  };
  return (
    <Card>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" fontWeight="bold">All 13 methods — today's reading, last signal, and how each has actually done on this ticker</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
            {params.definition}. <b>Base rate</b>: how often that move followed <i>any</i> bar ({base.n} bars) — the number a
            method must beat. Green = beats base rate with ≥{params.min_firings} firings; ⚠ = too few firings to trust either way. Hover a method name for what it looks for.
          </Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { py: 0.6 } }}>
          <TableHead><TableRow>
            {['Method', 'School', "Today's reading", 'Last signal', 'Turn UP: hit rate vs base (lift)', 'Turn DOWN: hit rate vs base (lift)'].map(h => (
              <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.72rem' }}>{h}</TableCell>
            ))}
          </TableRow></TableHead>
          <TableBody>
            {Object.entries(det).map(([k, d]: any) => (
              <TableRow key={k} hover>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                  <Tooltip title={d.what} placement="right"><span style={{ cursor: 'help', borderBottom: '1px dotted' }}>{d.name}</span></Tooltip>
                </TableCell>
                <TableCell sx={{ fontSize: '0.72rem' }}><Chip size="small" label={d.family} sx={{ height: 18, fontSize: '0.62rem' }} /></TableCell>
                <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{d.reading}</TableCell>
                <TableCell sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                  {d.last_signal ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DirChip d={d.last_signal.direction} label={d.last_signal.direction === 'up' ? 'UP' : 'DOWN'} />
                      <span style={{ fontWeight: d.last_signal.bars_ago <= 5 ? 700 : 400 }}>{d.last_signal.bars_ago === 0 ? 'today' : `${d.last_signal.bars_ago} bars ago`}</span>
                      <span style={{ opacity: 0.5 }}>{d.last_signal.date}</span>
                    </Box>
                  ) : <span style={{ opacity: 0.5 }}>never</span>}
                </TableCell>
                <Cell side="up" d={d} />
                <Cell side="down" d={d} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </CardContent>
    </Card>
  );
}

function Proven({ proven, base }: { proven: any; base: any }) {
  const Side = ({ d, title }: { d: 'up' | 'down'; title: string }) => (
    <Box sx={{ flex: 1, minWidth: 260 }}>
      <Typography variant="caption" fontWeight="bold" sx={{ color: dirColor(d) }}>{title} · base rate {pct(base[d])}</Typography>
      {proven[d].length === 0 ? (
        <Typography variant="body2" color="text.disabled">None — no detector beats the base rate at 95% confidence for this direction on this ticker.</Typography>
      ) : proven[d].map((p: any) => (
        <Box key={p.detector} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.3 }}>
          <Typography variant="body2" sx={{ minWidth: 200 }}>{p.name}</Typography>
          <Typography variant="body2" fontWeight="bold" sx={{ color: dirColor(d) }}>{pct(p.hit_rate_pct)}</Typography>
          <Typography variant="caption" color="text.secondary">+{p.lift} pts · n={p.firings} · CI {p.ci95[0]}–{p.ci95[1]}%</Typography>
        </Box>
      ))}
    </Box>
  );
  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider' }}><CardContent>
      <Typography variant="subtitle2" fontWeight="bold">Proven on this ticker — hit rate above base rate at 95% confidence</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        The competition, settled by evidence: only these detectors have earned the verdict for this direction on this history. More history raises n; it does not manufacture an edge.
      </Typography>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Side d="up" title="Turn UP (bottoms)" />
        <Side d="down" title="Turn DOWN (tops)" />
      </Box>
    </CardContent></Card>
  );
}

function Chart({ chart }: { chart: any }) {
  const data = chart.series.map((s: any) => {
    const m = chart.markers.filter((x: any) => x.date === s.date);
    return { ...s, up: m.some((x: any) => x.direction === 'up') ? s.low * 0.985 : null,
      down: m.some((x: any) => x.direction === 'down') ? s.high * 1.015 : null,
      tdLabel: Math.abs(s.td) === 9 ? '9' : Math.abs(s.td) >= 1 && Math.abs(s.td) <= 8 ? String(Math.abs(s.td)) : '' };
  });
  return (
    <Card><CardContent>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Last 250 bars — price, Bollinger 20/2σ, TDST lines, detector firings</Typography>
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={data} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="2 4" opacity={0.25} />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
          <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} width={54} />
          <RTooltip contentStyle={{ fontSize: 12 }} formatter={(v: any, name: string) => [typeof v === 'number' ? v.toFixed(2) : v, name]}
            labelFormatter={(l: any, p: any) => { const d = p?.[0]?.payload; return d ? `${l} · TD ${d.td > 0 ? '+' : ''}${d.td} · RSI ${d.rsi}` : l; }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="bb_upper" stroke="#64748B" dot={false} strokeWidth={1} strokeDasharray="3 3" name="BB upper" connectNulls />
          <Line type="monotone" dataKey="bb_lower" stroke="#64748B" dot={false} strokeWidth={1} strokeDasharray="3 3" name="BB lower" connectNulls />
          <Line type="monotone" dataKey="close" stroke="#60A5FA" dot={false} strokeWidth={1.6} name="Close" />
          {chart.tdst.map((t: any, i: number) => (
            <ReferenceLine key={i} y={t.level} stroke={t.direction === 'buy' ? '#EF4444' : '#10B981'} strokeDasharray="6 3" strokeOpacity={0.6}
              label={{ value: `TDST ${t.level}`, fontSize: 9, position: 'insideRight', fill: t.direction === 'buy' ? '#EF4444' : '#10B981' }} />
          ))}
          <Scatter dataKey="up" fill="#10B981" shape="triangle" name="turn-up signal" />
          <Scatter dataKey="down" fill="#EF4444" shape="triangle" name="turn-down signal" />
        </ComposedChart>
      </ResponsiveContainer>
      <Typography variant="caption" color="text.disabled">Red dashed = TDST resistance from a buy setup · green dashed = TDST support from a sell setup. Hover a bar for its TD count.</Typography>
    </CardContent></Card>
  );
}

function History({ rows }: { rows: any[] }) {
  const Res = ({ v }: { v: boolean | null }) => v == null ? <HelpOutline sx={{ fontSize: 14, color: 'text.disabled' }} />
    : v ? <CheckCircle sx={{ fontSize: 14, color: '#10B981' }} /> : <Cancel sx={{ fontSize: 14, color: '#EF4444' }} />;
  return (
    <Card><CardContent sx={{ p: 0 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ p: 1.5 }}>TD setups in the window — and what price did next</Typography>
      <Table size="small">
        <TableHead><TableRow>
          {['Setup', 'Completed', 'Perfected', 'TDST', 'Countdown', 'Turned after 9?', 'Move after 9', 'Turned after 13?'].map(h => (
            <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{h}</TableCell>
          ))}
        </TableRow></TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} hover>
              <TableCell><DirChip d={r.direction === 'buy' ? 'up' : 'down'} label={`${r.direction.toUpperCase()} 9`} /></TableCell>
              <TableCell sx={{ fontSize: '0.75rem' }}>{r.setup_date} <span style={{ opacity: 0.6 }}>@ ${r.close_at_9}</span></TableCell>
              <TableCell>{r.perfected ? <CheckCircle sx={{ fontSize: 14, color: '#10B981' }} /> : <Cancel sx={{ fontSize: 14, color: 'text.disabled' }} />}</TableCell>
              <TableCell sx={{ fontSize: '0.72rem' }}>${r.tdst}{r.tdst_broken ? <span style={{ color: '#EF4444' }}> broken {r.tdst_broken}</span> : ''}</TableCell>
              <TableCell sx={{ fontSize: '0.72rem' }}>{r.countdown}/13{r.countdown_done ? ` · done ${r.countdown_done}${r.countdown_qualified === false ? ' (unqualified)' : ''}` : ''}</TableCell>
              <TableCell><Tooltip title={`needed ≥ ${r.required_move_pct}% within the horizon`}><span><Res v={r.reversed_after_9} /></span></Tooltip></TableCell>
              <TableCell sx={{ fontSize: '0.75rem', color: r.move_after_9_pct == null ? undefined : (r.direction === 'buy' ? r.move_after_9_pct > 0 : r.move_after_9_pct < 0) ? '#10B981' : '#EF4444' }}>
                {r.move_after_9_pct == null ? '—' : `${r.move_after_9_pct > 0 ? '+' : ''}${r.move_after_9_pct}%`}
              </TableCell>
              <TableCell><Res v={r.reversed_after_13} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent></Card>
  );
}

export default function TurningPoints() {
  const [symbol, setSymbol] = useState('');
  const [lookback, setLookback] = useState('2 Y');
  const [timeframe, setTimeframe] = useState('1d');
  const [analyzed, setAnalyzed] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [res, setRes] = useState<any>(null);

  const run = async () => {
    if (!symbol.trim()) return;
    setLoading(true); setError(null);
    try { setRes(await apiService.getTurningPoints(symbol.trim().toUpperCase(), lookback, 2.0, 10, timeframe)); setAnalyzed(symbol.trim().toUpperCase()); }
    catch (e: any) { setError(e?.response?.data?.detail || e.message); setRes(null); }
    finally { setLoading(false); }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold">Turning Points</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 980, lineHeight: 1.6 }}>
        Discover when a stock, an index or a currency pair is about to change direction — on any timeframe from 2 minutes to
        yearly (intraday bars come from the Market Data subscriptions; daily and above from the daily history). Base algorithm:
        DeMark's <b>TD Sequential</b> — Setup 9 with <b>perfection</b>, <b>TDST</b> lines and Countdown 13 — competing with
        eleven other exhaustion methods from different schools: RSI divergence, Bollinger re-entry, volume climax, Fisher Transform,
        CUSUM change-point, Connors RSI(2), ADX exhaustion, Wyckoff failed breakout, key reversal bar, return z-score, and a
        walk-forward machine-learning ensemble. Every one is measured on the ticker's own history against the base rate;
        only those that beat it at 95% confidence are called <b>proven</b>. Use 5–10 years for a meaningful sample.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <TextField size="small" label="Ticker or index" placeholder="IONQ, SPY, ^GSPC" value={symbol}
          onChange={e => setSymbol(e.target.value)} onKeyDown={e => e.key === 'Enter' && run()} sx={{ width: 200 }} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Timeframe</InputLabel>
          <Select value={timeframe} label="Timeframe" onChange={e => setTimeframe(e.target.value)}>
            {[['2m', '2 min'], ['5m', '5 min'], ['15m', '15 min'], ['60m', '1 hour'], ['360m', '6 hours'], ['1d', 'Daily'], ['1w', 'Weekly'], ['1M', 'Monthly'], ['1Q', 'Quarterly'], ['1Y', 'Yearly']].map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }} disabled={!['1d', '1w', '1M', '1Q', '1Y'].includes(timeframe)}>
          <InputLabel>History</InputLabel>
          <Select value={lookback} label="History" onChange={e => setLookback(e.target.value)}>
            {['1 Y', '2 Y', '3 Y', '5 Y', '10 Y'].map(v => <MenuItem key={v} value={v}>{v.replace(' Y', ' year')}{v !== '1 Y' ? 's' : ''}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Search />} onClick={run} disabled={loading || !symbol.trim()}>
          Analyze
        </Button>
        {res && <Typography variant="caption" color="text.disabled">{res.symbol} · {res.bars} {timeframe} bars · {res.first_date} → {res.as_of}{res.intraday ? ' UTC' : ''} · last close {res.last_close}</Typography>}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {res && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}><Verdict cur={res.current} /></Grid>
          <Grid item xs={12} md={7}><TDState cur={res.current} /></Grid>
          <Grid item xs={12}><ExhaustionHeatmap symbol={analyzed} /></Grid>
          <Grid item xs={12}><Measured det={res.detectors} base={res.base_rate} params={res.params} /></Grid>
          <Grid item xs={12}><Proven proven={res.proven} base={res.base_rate} /></Grid>
          <Grid item xs={12}><Chart chart={res.chart} /></Grid>
          <Grid item xs={12}><History rows={res.history} /></Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
              <InfoOutlined sx={{ fontSize: 14 }} />
              <span>
                The detectors are counted, never averaged into a score: "turn UP likely" means two or more said up and none said down.
                A detector whose hit rate does not clear the base rate on this ticker is shown as such — a reading from it is a
                coincidence until proven otherwise. Daily bars only; not financial advice.
              </span>
            </Typography>
          </Grid>
        </Grid>
      )}

      <Accordion defaultExpanded={!res} sx={{ mt: 2, border: '1px solid', borderColor: '#F59E0B55', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEvents sx={{ color: '#F59E0B', fontSize: 18 }} />
            <Typography variant="subtitle2" fontWeight="bold">Additional info — which method wins where (from the methods race)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails><WinnersSummary /></AccordionDetails>
      </Accordion>
    </Box>
  );
}
